import {Calculator} from 'lucide-react'
import { useMemo, useState } from 'react'
import { formatEthSpot, formatNumber } from '@/lib/format'
import {
  CREATOR_SHARE_BPS,
  CURVE_SUPPLY,
  GRADUATION_QUOTE_ETH,
  GRADUATION_QUOTE_WEI,
  LAUNCH_FEE_ETH,
  PHANTOM_QUOTE_ETH,
  PLATFORM_SHARE_BPS,
  SNIPE_TAX_DURATION_MS,
  SNIPE_TAX_START_BPS,
  TOTAL_SUPPLY,
  TRADE_FEE_BPS,
  WAD,
} from '@/lib/launchpad/constants'
import {
  buyOnCurve,
  createLaunchState,
  getAmountIn,
  getAmountOut,
  graduate,
  remainingCurveTokens,
  sellOnCurve,
  spotPriceWei,
} from '@/lib/launchpad/curveMath'
import { parseEthToWei, weiToEthNumber, weiToEthString } from '@/lib/launchpad/wei'
import { formatBps } from '@/lib/protocol'

type Mode = 'buy' | 'sell'

function demoLaunchAtRaised(raisedWei: bigint) {
  const createdAtMs = Date.now() - SNIPE_TAX_DURATION_MS - 1
  const state = createLaunchState({
    id: 'demo-curve',
    name: 'Demo',
    ticker: 'DEMO',
    imageUrl: '',
    creatorAddress: '0x000000000000000000000000000000000000dEmo',
    createdAtMs,
  })
  const net = raisedWei > GRADUATION_QUOTE_WEI ? GRADUATION_QUOTE_WEI : raisedWei
  if (net <= 0n)
    return state
  try {
    const remaining = remainingCurveTokens(state)
    let tokensOut = getAmountOut(net, state.virtualQuote, state.virtualToken)
    let netUsed = net
    if (tokensOut > remaining) {
      tokensOut = remaining
      netUsed = getAmountIn(tokensOut, state.virtualQuote, state.virtualToken)
    }
    const next = {
      ...state,
      virtualQuote: state.virtualQuote + netUsed,
      virtualToken: state.virtualToken - tokensOut,
      realQuoteRaised: netUsed,
      tokensSold: tokensOut,
    }
    if (next.realQuoteRaised >= GRADUATION_QUOTE_WEI)
      return graduate(next)
    return next
  }
  catch {
    return state
  }
}

function formatTokens(wei: bigint): string {
  if (wei <= 0n)
    return '0'
  const whole = wei / WAD
  if (whole >= 1_000n)
    return formatNumber(Number(whole))
  return weiToEthString(wei, 6)
}

function safeParseEth(input: string): bigint {
  try {
    return parseEthToWei(input)
  }
  catch {
    return 0n
  }
}

function BondingCurveCalculator() {
  const [raisedInput, setRaisedInput] = useState('0')
  const [amount, setAmount] = useState('0.1')
  const [mode, setMode] = useState<Mode>('buy')

  const raisedWei = safeParseEth(raisedInput)
  const amountWei = safeParseEth(amount)

  const snapshot = useMemo(() => demoLaunchAtRaised(raisedWei), [raisedWei])

  const quote = useMemo(() => {
    const now = Date.now()
    const spotBefore = spotPriceWei(snapshot)
    const raisedBefore = snapshot.realQuoteRaised
    const remainingBefore = remainingCurveTokens(snapshot)

    if (amountWei <= 0n) {
      return {
        ok: true as const,
        error: null as string | null,
        tokens: 0n,
        ethIn: 0n,
        ethOut: 0n,
        tradeFee: 0n,
        creatorCut: 0n,
        platformCut: 0n,
        snipeCut: 0n,
        spotBefore,
        spotAfter: spotBefore,
        raisedAfter: raisedBefore,
        remainingAfter: remainingBefore,
        graduated: snapshot.status === 'graduated',
      }
    }

    try {
      if (mode === 'buy') {
        if (snapshot.status !== 'curve') {
          return {
            ok: false as const,
            error: 'This demo quotes the curve phase only. At 4.2 ETH raised, the launch graduates into the pool.',
            tokens: 0n,
            ethIn: 0n,
            ethOut: 0n,
            tradeFee: 0n,
            creatorCut: 0n,
            platformCut: 0n,
            snipeCut: 0n,
            spotBefore,
            spotAfter: spotBefore,
            raisedAfter: raisedBefore,
            remainingAfter: remainingBefore,
            graduated: true,
          }
        }
        const { state, result } = buyOnCurve(snapshot, amountWei, now)
        return {
          ok: true as const,
          error: null as string | null,
          tokens: result.tokensOut,
          ethIn: result.quoteSpent,
          ethOut: 0n,
          tradeFee: result.tradeFee,
          creatorCut: result.creatorCut,
          platformCut: result.platformCut,
          snipeCut: result.snipeCut,
          spotBefore,
          spotAfter: spotPriceWei(state),
          raisedAfter: state.realQuoteRaised,
          remainingAfter: remainingCurveTokens(state),
          graduated: result.graduated,
        }
      }

      const { state, result } = sellOnCurve(snapshot, amountWei)
      return {
        ok: true as const,
        error: null as string | null,
        tokens: result.tokenIn,
        ethIn: 0n,
        ethOut: result.userReceives,
        tradeFee: result.tradeFee,
        creatorCut: result.creatorCut,
        platformCut: result.platformCut,
        snipeCut: result.snipeCut,
        spotBefore,
        spotAfter: spotPriceWei(state),
        raisedAfter: state.realQuoteRaised,
        remainingAfter: remainingCurveTokens(state),
        graduated: false,
      }
    }
    catch (err) {
      return {
        ok: false as const,
        error: err instanceof Error ? err.message : 'This size cannot be filled on the current curve.',
        tokens: 0n,
        ethIn: 0n,
        ethOut: 0n,
        tradeFee: 0n,
        creatorCut: 0n,
        platformCut: 0n,
        snipeCut: 0n,
        spotBefore,
        spotAfter: spotBefore,
        raisedAfter: raisedBefore,
        remainingAfter: remainingBefore,
        graduated: snapshot.status === 'graduated',
      }
    }
  }, [amountWei, mode, snapshot])

  const raisedEth = weiToEthNumber(snapshot.realQuoteRaised)
  const toGraduate = Math.max(0, GRADUATION_QUOTE_ETH - raisedEth)
  const avgBuy = quote.tokens > 0n && quote.ethIn > 0n
    ? weiToEthNumber(quote.ethIn) / weiToEthNumber(quote.tokens)
    : 0
  const avgSell = quote.tokens > 0n && quote.ethOut > 0n
    ? weiToEthNumber(quote.ethOut) / weiToEthNumber(quote.tokens)
    : 0

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md sm:p-6">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
          <Calculator className="h-4 w-4" aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Bonding Curve Calculator</h3>
          <p className="text-xs text-zinc-500">
            Live Penguin Pad curve: constant-product, 1% quote fee, {GRADUATION_QUOTE_ETH} ETH graduation
          </p>
        </div>
      </div>

      <dl className="mt-5 grid gap-2 sm:grid-cols-3">
        <Fact label="Launch fee" value={`${LAUNCH_FEE_ETH} ETH`} />
        <Fact label="Trade fee" value={`${formatBps(TRADE_FEE_BPS)} of ETH`} />
        <Fact label="Fee split" value={`${formatBps(CREATOR_SHARE_BPS)} creator / ${formatBps(PLATFORM_SHARE_BPS)} pad`} />
        <Fact label="Phantom reserve" value={`${PHANTOM_QUOTE_ETH} ETH`} />
        <Fact label="Curve supply" value={`${formatNumber(Number(CURVE_SUPPLY / WAD))} / ${formatNumber(Number(TOTAL_SUPPLY / WAD))}`} />
        <Fact label="Anti-snipe" value={`${formatBps(SNIPE_TAX_START_BPS)} → 0 in ${SNIPE_TAX_DURATION_MS / 1000}s`} />
      </dl>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="curve-raised" className="mb-1.5 block text-xs font-medium text-zinc-400">
            ETH already raised (net into the curve)
          </label>
          <input
            id="curve-raised"
            type="number"
            step="0.01"
            min="0"
            max={GRADUATION_QUOTE_ETH}
            value={raisedInput}
            onChange={e => setRaisedInput(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-sm text-white focus:border-lime-400/50 focus:outline-none focus:ring-1 focus:ring-lime-400/30"
          />
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">Spot now (pre-fee)</p>
          <p className="mt-1 text-lg font-bold text-white">{formatEthSpot(weiToEthNumber(quote.spotBefore))}</p>
          <p className="mt-1 text-[11px] text-zinc-500">
            {formatEthSpot(raisedEth)}
            {' '}
            raised ·
            {' '}
            {formatEthSpot(toGraduate)}
            {' '}
            to graduate
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-3 inline-flex rounded-full border border-white/10 bg-black/50 p-1" role="tablist" aria-label="Calculator mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'buy'}
            onClick={() => { setMode('buy'); setAmount('0.1') }}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
              mode === 'buy' ? 'bg-lime-400 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            Buy with ETH
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'sell'}
            onClick={() => { setMode('sell'); setAmount('1000000') }}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
              mode === 'sell' ? 'bg-lime-400 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            Sell tokens
          </button>
        </div>

        <label htmlFor="curve-amount" className="mb-1.5 block text-xs font-medium text-zinc-400">
          {mode === 'buy' ? 'ETH to spend' : 'Tokens to sell'}
        </label>
        <input
          id="curve-amount"
          type="number"
          step={mode === 'buy' ? '0.01' : '1'}
          min="0"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-sm text-white focus:border-lime-400/50 focus:outline-none focus:ring-1 focus:ring-lime-400/30"
        />
        <p className="mt-1.5 text-[11px] text-zinc-500">
          Same fill as a live quick launch: 1% comes off ETH first, then Uniswap-style amount-out. Raised is net ETH in the curve. Anti-snipe is off here (after 5s).
        </p>
      </div>

      {quote.error
        ? (
            <p className="mt-4 rounded-2xl border border-red-400/20 bg-red-400/5 px-3 py-2 text-xs text-red-300" role="alert">
              {quote.error}
            </p>
          )
        : null}

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">{mode === 'buy' ? 'Tokens received' : 'ETH received'}</p>
          <p className="mt-1 text-lg font-bold text-lime-400">
            {mode === 'buy' ? formatTokens(quote.tokens) : formatEthSpot(weiToEthNumber(quote.ethOut))}
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">Average fill</p>
          <p className="mt-1 text-lg font-bold text-white">
            {formatEthSpot(mode === 'buy' ? avgBuy : avgSell)}
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">Spot after trade</p>
          <p className="mt-1 text-lg font-bold text-orange-400">{formatEthSpot(weiToEthNumber(quote.spotAfter))}</p>
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">1% trade fee</p>
          <p className="mt-1 text-sm font-semibold text-white">{formatEthSpot(weiToEthNumber(quote.tradeFee))}</p>
          <p className="mt-1 text-[11px] text-zinc-500">
            {formatEthSpot(weiToEthNumber(quote.creatorCut))}
            {' '}
            creator ·
            {' '}
            {formatEthSpot(weiToEthNumber(quote.platformCut))}
            {' '}
            pad
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">ETH into curve</p>
          <p className="mt-1 text-sm font-semibold text-white">{formatEthSpot(weiToEthNumber(quote.raisedAfter))}</p>
          <p className="mt-1 text-[11px] text-zinc-500">
            Target
            {' '}
            {GRADUATION_QUOTE_ETH}
            {' '}
            ETH (
            {formatEthSpot(weiToEthNumber(GRADUATION_QUOTE_WEI))}
            )
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
          <p className="text-xs text-zinc-500">Curve tokens left</p>
          <p className="mt-1 text-sm font-semibold text-white">{formatTokens(quote.remainingAfter)}</p>
          <p className="mt-1 text-[11px] text-zinc-500">
            {quote.graduated ? 'This buy graduates the curve.' : 'Unsold curve supply seeds LP at graduation.'}
          </p>
        </div>
      </div>
    </div>
  )
}

function Fact({ label, value }: { label: string, value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-2">
      <dt className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">{label}</dt>
      <dd className="mt-0.5 text-xs font-medium text-zinc-200">{value}</dd>
    </div>
  )
}

export default BondingCurveCalculator
